import React from "react";

/** Field names that should be read as money rather than plain numbers. */
const MONEY_HINT = /(amount|balance|total|debit|credit|value|price|cost|paid|due|budget|actual|revenue|expense|profit|fees|net|gross|subtotal|tax)/i;
const PERCENT_HINT = /(rate|margin|percent|pct|ratio)$/i;
const DATE_HINT = /(date|_at|period)$/i;

const isNumericLike = (v) =>
  typeof v === "number" || (typeof v === "string" && v.trim() !== "" && !Number.isNaN(Number(v)));

/** Human label for a snake_case / camelCase backend key. */
export const humanise = (key) =>
  String(key)
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b(id|tin|vat|ar|ap|gl|ytd|coa|nssf|paye|lst)\b/gi, (m) => m.toUpperCase())
    .replace(/^./, (c) => c.toUpperCase());

export const fmtVal = (v, key = "") => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";

  if (isNumericLike(v)) {
    const n = Number(v);
    if (PERCENT_HINT.test(key)) return `${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
    if (MONEY_HINT.test(key)) {
      return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return Number.isInteger(n) ? n.toLocaleString() : n.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }

  if (typeof v === "string" && DATE_HINT.test(key)) {
    const d = new Date(v.replace(" ", "T"));
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    }
  }

  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

/** Numeric columns are right-aligned so figures line up down the column. */
const numericColumn = (rows, key) =>
  rows.some((r) => isNumericLike(r[key])) &&
  rows.every((r) => r[key] === null || r[key] === undefined || r[key] === "" || isNumericLike(r[key]));

/**
 * Resilient report renderer — copes with any JSON `details` shape:
 * arrays-of-rows become tables, objects become key/value grids (recursively),
 * scalars render plainly. Lets report/analytics pages display backend output
 * without hardcoding every response schema.
 *
 * Presentation is inferred from the field names the backend uses: money and
 * percentage fields are formatted and right-aligned, dates are rendered in the
 * system's short form, and keys become readable headings — so a raw endpoint
 * response still reads like a finished report.
 */
const SmartReport = ({ data, depth = 0 }) => {
  if (data === null || data === undefined) return <div className="tx-muted">No data.</div>;

  if (Array.isArray(data)) {
    if (data.length === 0) return <div className="tx-muted">No rows.</div>;
    if (typeof data[0] !== "object" || data[0] === null) {
      return (
        <div className="d-flex flex-wrap gap-1">
          {data.map((v, i) => (
            <span key={i} className="badge bg-light text-dark">{fmtVal(v)}</span>
          ))}
        </div>
      );
    }
    // Union of keys — rows from some endpoints omit fields rather than nulling them.
    const cols = [...new Set(data.flatMap((r) => Object.keys(r || {})))];
    const numeric = Object.fromEntries(cols.map((c) => [c, numericColumn(data, c)]));
    return (
      <div className="table-responsive">
        <table className="table table-sm table-bordered align-middle mb-0">
          <thead className="bg-light">
            <tr>
              {cols.map((c) => (
                <th
                  key={c}
                  className={`tx-12 ${numeric[c] ? "text-end" : ""}`}
                  style={{ fontWeight: 600, whiteSpace: "nowrap" }}
                >
                  {humanise(c)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, i) => (
              <tr key={i}>
                {cols.map((c) => (
                  <td
                    key={c}
                    className={`tx-12 ${numeric[c] ? "text-end" : ""}`}
                    style={numeric[c] ? { fontVariantNumeric: "tabular-nums" } : undefined}
                  >
                    {row?.[c] && typeof row[c] === "object"
                      ? <SmartReport data={row[c]} depth={depth + 1} />
                      : fmtVal(row?.[c], c)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (typeof data === "object") {
    const entries = Object.entries(data);
    // Nested objects read better as their own labelled blocks than as a cell
    // crammed inside the parent's value column.
    const scalars = entries.filter(([, v]) => !v || typeof v !== "object");
    const nested = entries.filter(([, v]) => v && typeof v === "object");
    return (
      <>
        {scalars.length > 0 && (
          <table className="table table-sm mb-0">
            <tbody>
              {scalars.map(([k, v]) => (
                <tr key={k}>
                  <td className="fw-semibold tx-12" style={{ width: "38%" }}>{humanise(k)}</td>
                  <td
                    className={`tx-12 ${isNumericLike(v) ? "text-end" : ""}`}
                    style={isNumericLike(v) ? { fontVariantNumeric: "tabular-nums" } : undefined}
                  >
                    {fmtVal(v, k)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {nested.map(([k, v]) => (
          <div key={k} className={depth === 0 ? "mt-3" : "mt-2"}>
            <div className="fw-semibold tx-12 tx-uppercase tx-muted mb-1" style={{ letterSpacing: "0.05em" }}>
              {humanise(k)}
            </div>
            <SmartReport data={v} depth={depth + 1} />
          </div>
        ))}
      </>
    );
  }

  return <div>{fmtVal(data)}</div>;
};

export default SmartReport;
